import { ref } from "vue";
import { sendChatMessage } from "../assets/js/impactdriver_front/requests";
import { useChatStreaming } from "./useChatStreaming";
import { useMarkdown } from "./useMarkdown";

/**
 * Shared chat protocol for AIAsk (sidebar, per-customer) and GlobalAIChat
 * (floating, multi-customer). Owns conversation/streaming state plus the
 * send / loadHistory / clear protocol. Mode-specific bits — request shape,
 * history endpoints — are supplied by the caller.
 *
 * @param {Object}   options
 * @param {Function} options.buildRequestArgs - () => extra args for sendChatMessage (e.g. { customerCrmId } or { scope: 'global' }). Called fresh each send.
 * @param {Function} options.fetchHistory     - () => Promise<{ messages: [{role, content}] }>
 * @param {Function} options.clearHistory     - () => Promise<void>
 */
export function useAiChat({ buildRequestArgs, fetchHistory, clearHistory }) {
  const conversationHistory = ref([]);
  const isStreaming = ref(false);
  const streamingContent = ref("");
  const currentToolCall = ref(null);
  const errorMessage = ref("");

  /**
   * Send a message and stream the assistant response.
   *
   * @param {string} displayMessage - what the user sees in their bubble
   * @param {Object} [opts]
   * @param {string} [opts.wireMessage] - what is actually sent to the API
   *   (defaults to displayMessage). Use this when the component needs to
   *   prepend extra context, e.g. customer metadata for report mode.
   */
  async function send(displayMessage, { wireMessage } = {}) {
    isStreaming.value = true;
    streamingContent.value = "";
    currentToolCall.value = null;
    errorMessage.value = "";

    conversationHistory.value.push({ role: "user", content: displayMessage });

    try {
      const history = conversationHistory.value
        .slice(0, -1)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await sendChatMessage(wireMessage ?? displayMessage, {
        ...buildRequestArgs(),
        history,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const { processStreamingResponse } = useChatStreaming();

      const content = await processStreamingResponse(response, {
        onContent: (chunk) => {
          currentToolCall.value = null;
          streamingContent.value += chunk;
        },
        onToolCall: (_tools, status) => {
          currentToolCall.value = status || "Processing...";
          streamingContent.value = "";
        },
        onError: (message) => {
          errorMessage.value = message;
        },
      });

      if (content) {
        conversationHistory.value.push({ role: "assistant", content });
      }
    } catch (e) {
      errorMessage.value = e.message;
    } finally {
      isStreaming.value = false;
      streamingContent.value = "";
      currentToolCall.value = null;
    }
  }

  async function loadHistory() {
    try {
      const data = await fetchHistory();
      const messages = data?.messages ?? [];
      conversationHistory.value = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
    } catch (_) {
      errorMessage.value = "Failed to load chat history.";
    }
  }

  async function clear() {
    try {
      await clearHistory();
    } catch (_) {
      errorMessage.value = "Could not clear the saved conversation.";
    }
    conversationHistory.value = [];
    streamingContent.value = "";
    currentToolCall.value = null;
    errorMessage.value = "";
  }

  function renderMarkdown(content) {
    if (!content) return "";
    const stripped = content.replace(/```actions[\s\S]*?(?:```|$)/g, "");
    return useMarkdown().renderMarkdown(stripped);
  }

  return {
    conversationHistory,
    isStreaming,
    streamingContent,
    currentToolCall,
    errorMessage,
    send,
    loadHistory,
    clear,
    renderMarkdown,
  };
}
